import { useToken } from '@chakra-ui/react';
import {
  Box,
  Flex,
  IconButton,
  Icon,
  BoxProps,
  FlexProps,
} from '@everlywell/ui-kit';
import { CaretLeft, CaretRight, Hexagon } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { Mousewheel, FreeMode } from 'swiper/modules';
import { SwiperSlide, SwiperProps, SwiperClass } from 'swiper/react';

import { Swiper } from './Carousel.styles';

// Types ----------

export interface CarouselProps extends SwiperProps {
  children: React.ReactNode[];
  arrowInsideOffset?: boolean;
  arrowPosition?: 'sides' | 'bottom';
  insidePagination?: boolean;
  showPagination?: boolean;
  slideStyle?: React.CSSProperties;
  hasFadeOnEdges?: boolean;
  fadeBgColor?: BoxProps['backgroundColor'] & (string & {});
  paginationContainerStyles?: FlexProps;
}

// Carousel ------

const Carousel: React.FC<CarouselProps> = (props) => {
  const {
    arrowInsideOffset,
    children,
    insidePagination,
    showPagination,
    arrowPosition = 'sides',
    hasFadeOnEdges = false,
    fadeBgColor = 'tints.white',
    paginationContainerStyles = {},
    ...rest
  } = props;

  const [currentSwiperIndex, setCurrentSwiperIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [swiper, setSwiper] = React.useState<SwiperClass | null>(null);
  // adding useToken to support both hexadecimal and string values such as 'tints.white'
  const [carouselFadeBgColor] = useToken('colors', [fadeBgColor]);

  const initializeSwiper = (swiper: SwiperClass) => {
    props.onSwiper?.(swiper);
    setSwiper(swiper);
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    props.onSlideChange?.(swiper);
    setIsBeginning(swiper?.isBeginning);
    setIsEnd(swiper?.isEnd);
  };

  const handleSnapIndexChange = (swiper: SwiperClass) => {
    setCurrentSwiperIndex(swiper?.snapIndex);
  };

  const handleOnProgress = (swiper: SwiperClass) => {
    setIsBeginning(swiper?.isBeginning);
    setIsEnd(swiper?.isEnd);
  };

  const slides = children!.map((element, index) => (
    <SwiperSlide
      data-testid="swiper-slide"
      key={`swiper-slide-${index}`}
      style={props.slideStyle}
    >
      {element}
    </SwiperSlide>
  ));

  const totalBullets = swiper?.snapGrid?.length ?? slides.length;

  const insidePaginationWrapperStyles = {
    marginTop: 0,
  };

  const insidePaginationBulletStyles = {
    position: 'absolute',
    bottom: 4,
    zIndex: 10,
  };

  return (
    <Box
      role="region"
      aria-roledescription="carousel"
      aria-live="polite"
      aria-label="Image Carousel"
      position="relative"
      sx={
        hasFadeOnEdges
          ? {
              '&:after, &:before': {
                content: '""',
                position: 'absolute',
                width: '140px',
                height: `100%`,
                zIndex: 2,
                pointerEvents: 'none',
                transition: 'all 0.3s ease-in-out',
              },
              '&:before': {
                top: 0,
                left: 0,
                visibility: isBeginning ? 'hidden' : 'visible',
                opacity: isBeginning ? 0 : 1,
                background: `linear-gradient(270deg, rgba(255, 255, 255, 0.00) 10%, ${carouselFadeBgColor} 80%)`,
                transform: 'translateX(-25px)',
              },
              '&:after': {
                top: 0,
                right: 0,
                visibility: isEnd ? 'hidden' : 'visible',
                opacity: isEnd ? 0 : 1,
                background: `linear-gradient(90deg, rgba(255, 255, 255, 0.00) 10%, ${carouselFadeBgColor} 80%)`,
                transform: 'translateX(25px)',
              },
            }
          : {}
      }
    >
      <Swiper
        {...rest}
        data-testid="swiper-carousel"
        offsetSlide={true}
        onSwiper={initializeSwiper}
        onSlideChange={handleSlideChange}
        onSnapIndexChange={handleSnapIndexChange}
        onProgress={handleOnProgress}
        modules={[Mousewheel, FreeMode]}
      >
        {slides}
      </Swiper>

      {showPagination && totalBullets > 1 && (
        <Flex
          justifyContent={[
            'center',
            null,
            arrowPosition === 'sides' ? 'center' : 'space-between',
          ]}
          alignItems="center"
          marginTop={6}
          sx={insidePagination ? insidePaginationWrapperStyles : {}}
          {...paginationContainerStyles}
        >
          <Flex
            alignItems="center"
            gap={2}
            sx={insidePagination ? insidePaginationBulletStyles : {}}
          >
            {[...new Array(totalBullets)].map((_, index) =>
              index === currentSwiperIndex ? (
                <Icon
                  as={Hexagon}
                  color="viridian.base"
                  boxSize="16px"
                  sx={{ transform: 'rotate(90deg)', mx: -0.5 }}
                  weight="fill"
                  key={index}
                />
              ) : (
                <Box
                  key={index}
                  as="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => swiper?.slideTo(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor:
                      index === currentSwiperIndex
                        ? 'viridian.base'
                        : 'tints.medGrey',
                    border: '1px solid',
                    borderColor:
                      index === currentSwiperIndex
                        ? 'viridian.base'
                        : 'tints.medGrey',
                    cursor: 'pointer',
                  }}
                />
              ),
            )}
          </Flex>

          <Box
            display={{
              base: 'none',
              largeTablet: 'flex',
            }}
            sx={{
              ...(arrowPosition === 'sides' && {
                position: 'absolute',
                top: 'calc(50% - 36px)',
                zIndex: 1,
                justifyContent: 'space-between',
                width: '100%',
                pointerEvents: 'none',
              }),
            }}
          >
            <IconButton
              icon={<CaretLeft />}
              aria-label="Previous slide"
              onClick={() => swiper?.slidePrev()}
              variant="secondary"
              sx={{
                mr: 2,
                pointerEvents: 'all',
                marginLeft: arrowInsideOffset ? 4 : 0,
              }}
              isDisabled={isBeginning}
              size="sm"
            />
            <IconButton
              icon={<CaretRight />}
              aria-label="Next slide"
              onClick={() => swiper?.slideNext()}
              variant="secondary"
              isDisabled={isEnd}
              sx={{
                pointerEvents: 'all',
                marginRight: arrowInsideOffset ? 4 : 0,
              }}
              size="sm"
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};

// Export ---------

export default React.memo(Carousel);
